import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { AUTH_MODE } from '../../../apps/shutterbug/views/Login/constants';
import AuthModePhone from '../../../apps/shutterbug/views/Login/components/AuthModePhone';
import AuthModeEmail from '../../../apps/shutterbug/views/Login/components/AuthModeEmail';
import AuthModeEmailPassword from '../../../apps/shutterbug/views/Login/components/AuthModeEmailPassword';
import AuthModeEmailPin from '../../../apps/shutterbug/views/Login/components/AuthModeEmailPin';
import useLoginAuth from './hooks/useLoginAuth';
import { getErrorString } from '../../../util/normalize-errors';
import FormGroup from '../../global/FormGroup';
import Alert from '../../global/Alert';
import Button from '../../global/Button';
import Spinner from '../../global/Spinner';
import { formatPhone } from '../../../util/phone';
import useEmailPinAuth from '../hooks/useEmailPinAuth';

const propTypes = {
  onLoginCompleted: PropTypes.func,
  onRequestClose: PropTypes.func,
  message: PropTypes.string,
  defaultPhone: PropTypes.string,
  defaultEmailAddress: PropTypes.string,
};

const STEPS = {
  AUTH: 'AUTH',
  INFO: 'INFO',
};

const LoginAuth = ({
  onLoginCompleted,
  onRequestClose,
  message,
  defaultPhone,
  defaultEmailAddress,
}) => {
  const [step, setStep] = useState(STEPS.AUTH);
  const [hideToggles, setHideToggles] = useState(false);
  const [authMode, setAuthMode] = useState(AUTH_MODE.PHONE);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(defaultEmailAddress || '');
  const [password, setPassword] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    user,
    emailExists,
    onAccountLogin,
    onAccountUpdate,
    onLoginSuccess,
    onAttachRolesToAccount,
  } = useLoginAuth();
  const {
    onLoginByEmail,
    onEmailAuthenticationRequest: onSendPinEmail,
  } = useEmailPinAuth();

  useEffect(() => {
    const validUserData = !!user?.name && !!user?.email_address;

    if (user && validUserData) {
      onAttachRolesToAccount();
      onLoginCompleted?.();
      onRequestClose?.();
    } else if (user) {
      setStep(STEPS.INFO);
    }
  }, [user]);

  useEffect(() => {
    if (authMode !== AUTH_MODE.PHONE) {
      setHideToggles(false);
    }
  }, [authMode]);

  const attemptEmailPasswordLogin = async () => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await onAccountLogin(email, password);
      onLoginSuccess();
    } catch (e) {
      setError(getErrorString(e));
    } finally {
      setLoading(false);
    }
  };

  const attemptEmailPinLogin = async () => {
    setLoading(true);
    setError('');

    try {
      await onLoginByEmail({ emailAddress: email, code: pinCode });
      onLoginSuccess();
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptAccountUpdate = async () => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const isEmailExists = await emailExists(email);
      if (isEmailExists) {
        throw new Error("The email address you've entered already exists.");
      }

      await onAccountUpdate({
        id: user.id,
        name: `${firstName} ${lastName}`.trim(),
        email_address: email,
      });

      onLoginCompleted?.();
      onRequestClose?.();
    } catch (e) {
      setError(getErrorString(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="shutterbug-login">
        <div className="login-form">
          {step === STEPS.AUTH && (
            <Fragment>
              {!hideToggles && (
                <h3 className="text-center">
                  {message ? <>{message}</> : <>Log in to Waldo</>}
                </h3>
              )}
              <div>
                {authMode === AUTH_MODE.PHONE && (
                  <AuthModePhone
                    defaultPhone={defaultPhone}
                    onSentPin={() => setHideToggles(true)}
                    allowEmailAuth={!hideToggles}
                    onEmailAuth={() => {
                      setAuthMode(AUTH_MODE.EMAIL);
                    }}
                    phoneAuthProps={{
                      verificationText: ({ phone }) => (
                        <h3>
                          Please enter the code that was sent to: <br />{' '}
                          <span className="text-primary">
                            {formatPhone(phone)}
                          </span>
                        </h3>
                      ),
                    }}
                  />
                )}
                {authMode === AUTH_MODE.EMAIL && (
                  <AuthModeEmail
                    onSubmit={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      setAuthMode(AUTH_MODE.EMAIL_PASSWORD);
                    }}
                    values={{
                      email,
                    }}
                    onChange={(key, value) => {
                      if (key === 'email') {
                        setEmail(value);
                      }
                    }}
                    error={error}
                    loading={loading}
                    isFormValid={email !== ''}
                    onSendMagicLink={() => {
                      setAuthMode(AUTH_MODE.EMAIL_LINK);
                    }}
                  />
                )}
                {authMode === AUTH_MODE.EMAIL_PASSWORD && (
                  <AuthModeEmailPassword
                    onSubmit={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      attemptEmailPasswordLogin();
                    }}
                    values={{
                      password,
                    }}
                    onChange={(key, value) => {
                      if (key === 'password') {
                        setPassword(value);
                      }
                    }}
                    error={error}
                    loading={loading}
                    isFormValid={password !== ''}
                    onEmailPinRequest={() => {
                      onSendPinEmail({ emailAddress: email });
                      setAuthMode(AUTH_MODE.EMAIL_PIN);
                    }}
                  />
                )}
                {authMode === AUTH_MODE.EMAIL_PIN && (
                  <AuthModeEmailPin
                    onSubmit={event => {
                      event.preventDefault();
                      attemptEmailPinLogin();
                    }}
                    values={{
                      pinCode,
                    }}
                    onChange={(key, value) => {
                      if (key === 'pinCode') {
                        setPinCode(value);
                      }
                    }}
                    error={error}
                    loading={loading}
                    isFormValid={pinCode !== ''}
                    onResendEmail={() =>
                      onSendPinEmail({ emailAddress: email })
                    }
                  />
                )}
              </div>
            </Fragment>
          )}
          {step === STEPS.INFO && (
            <Fragment>
              <h3>Complete your profile below.</h3>
              <form
                className="login-form"
                onSubmit={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  attemptAccountUpdate();
                }}
              >
                <div className="row">
                  <div className="col-md-6 col-12">
                    <FormGroup
                      id="first_name"
                      name="first_name"
                      label="First name"
                      placeholder="John"
                      value={firstName}
                      onChange={({ target }) => setFirstName(target.value)}
                      wrapperClassName="mb-1"
                    />
                  </div>
                  <div className="col-md-6 col-12">
                    <FormGroup
                      id="last_name"
                      name="last_name"
                      label="Last name"
                      placeholder="Smith"
                      value={lastName}
                      onChange={({ target }) => setLastName(target.value)}
                      wrapperClassName="mb-1"
                    />
                  </div>
                  <div className="col-12">
                    <FormGroup
                      id="email"
                      name="email"
                      label="Email address"
                      placeholder="your@email.com"
                      value={email}
                      onChange={({ target }) => setEmail(target.value)}
                      wrapperClassName="mb-1"
                    />
                  </div>
                  <div className="col-12">
                    {error && (
                      <Alert type="danger" className="mt-2">
                        <strong>Update wasn&apos;t successful</strong>
                        <br />
                        {error}
                      </Alert>
                    )}
                  </div>
                  <div className="col-12">
                    <Button
                      type="submit"
                      disabled={loading || !firstName || !lastName || !email}
                    >
                      {!loading ? 'Save' : <Spinner light size="xs" />}
                    </Button>
                  </div>
                </div>
              </form>
            </Fragment>
          )}
        </div>
      </div>
    </Fragment>
  );
};

LoginAuth.propTypes = propTypes;

export default LoginAuth;
